import { useState, type FormEvent } from 'react';
import { Alerte, Bouton, Carte, Champ } from '../ui';
import { FournisseursTiers } from '../auth/Fournisseurs';
import { EcranEntree } from '../auth/EcranEntree';
import { messageDErreur, useSession } from '../auth/session';
import { useTraduction } from '../i18n/langue';

/**
 * Création de compte.
 *
 * L'API répond 202 quelle que soit l'issue : elle ne dit jamais si une adresse est déjà
 * inscrite. L'écran suit la même règle — annoncer « cette adresse existe déjà » ici
 * rétablirait l'oracle que le serveur refuse (docs/02 §1, docs/05 §3).
 */
export function Inscription({
  surSucces,
  surConnexion,
  surSession,
}: {
  surSucces?: () => void;
  surConnexion?: () => void;
  /**
   * S'inscrire par un fournisseur tiers, dans la coque native, **ouvre une session** :
   * il n'y a pas d'e-mail à confirmer, le fournisseur a déjà répondu de l'adresse. La
   * suite est donc celle d'une connexion, pas celle d'un formulaire envoyé.
   */
  surSession?: () => void;
}) {
  const { client } = useSession();
  const { locale, messages } = useTraduction();
  const m = messages.entree.inscription;
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoye, setEnvoye] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setErreur(null);

    if (motDePasse.length < 12) {
      setErreur(m.motDePasseTropCourt);
      return;
    }

    setEnvoi(true);
    try {
      await client.request('/auth/register', {
        method: 'POST',
        body: { email, password: motDePasse, displayName: nom, locale },
        skipRefresh: true,
      });
      setEnvoye(true);
      surSucces?.();
    } catch (cause) {
      setErreur(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  }

  if (envoye) {
    return (
      <>
        <h1>{m.verifiezBoite}</h1>
        <Carte>
          <p style={{ marginTop: 0 }}>{m.siAdresseUtilisable}</p>
          <p style={{ marginBottom: 0 }} className="discret">
            {m.dejaConnexionPossible}
          </p>
        </Carte>
      </>
    );
  }

  return (
    <EcranEntree
      titre={m.titre}
      alternative={
        <>
          <p>{m.dejaCompte}</p>
          <Bouton variante="lien" onClick={() => surConnexion?.()}>
            {m.seConnecter}
          </Bouton>
        </>
      }
    >
      <Carte>
        <form onSubmit={(e) => void soumettre(e)} noValidate>
          <Champ
            label={m.prenom}
            name="displayName"
            autoComplete="given-name"
            required
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            aide={m.prenomAide}
          />
          <Champ
            label={m.email}
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Champ
            label={m.motDePasse}
            type="password"
            name="password"
            autoComplete="new-password"
            required
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            aide={m.motDePasseAide}
          />

          {erreur ? <Alerte titre={m.erreurTitre}>{erreur}</Alerte> : null}

          <Bouton type="submit" disabled={envoi}>
            {envoi ? m.creationEnCours : m.creerMonCompte}
          </Bouton>
        </form>

        <FournisseursTiers action="inscription" surSession={() => surSession?.()} />
      </Carte>
    </EcranEntree>
  );
}
